import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Reveals streamed text at a steady, framerate-independent cadence — one
 * character at a time — so text that arrives in uneven, network-timed chunks
 * (often split mid-word) flows out smoothly instead of popping in as ragged
 * half-words.
 *
 * Usage: feed received text via `push()`, render `text`, and call `finish()`
 * when the stream closes to let the reveal drain before handing off to the
 * persisted copy. `reset()` wipes everything (e.g. on unmount / context switch).
 *
 * Pass `enabled: false` to turn the animation off entirely: `push()` then
 * reveals text instantly and `isRevealing` stays false, so a caller can toggle
 * the effect without any other code changes.
 */
interface UseTypewriterOptions {
  /** When false, text is revealed instantly with no animation. Default true. */
  enabled?: boolean
  /** Floor reveal speed (chars/sec) so the tail of a reply never crawls. */
  minCharsPerSec?: number
  /** How aggressively the reveal speeds up when it falls behind the incoming
   *  stream — chars/sec added per buffered character. Higher catches up faster
   *  on fast replies without ever dumping a chunk on screen. */
  backlogGain?: number
}

interface UseTypewriter {
  /** The text revealed so far — render this. */
  text: string
  /** True while the reveal is still catching up to the received text. */
  isRevealing: boolean
  /** Append received text to the buffer; starts the reveal if idle. */
  push: (chunk: string) => void
  /** Clear the buffer + revealed text and stop the reveal immediately. */
  reset: () => void
  /** Let the reveal drain to the end, then run `onDrained` and clear. Runs
   *  immediately if already fully revealed, if the buffer is empty, or if the
   *  effect is disabled. */
  finish: (onDrained?: () => void) => void
}

const DEFAULT_MIN_CHARS_PER_SEC = 48
const DEFAULT_BACKLOG_GAIN = 4.8

export function useTypewriter({
  enabled = true,
  minCharsPerSec = DEFAULT_MIN_CHARS_PER_SEC,
  backlogGain = DEFAULT_BACKLOG_GAIN,
}: UseTypewriterOptions = {}): UseTypewriter {
  const [text, setText] = useState('')
  const [isRevealing, setIsRevealing] = useState(false)

  // Full text received so far (grows as tokens arrive); how much of it is shown.
  const targetRef = useRef('')
  const displayedLenRef = useRef(0)
  const rafIdRef = useRef<number | null>(null)
  const lastTickRef = useRef(0)
  // Deferred hand-off run once the reveal reaches the end of the buffer.
  const onDrainRef = useRef<(() => void) | null>(null)
  // Holds the latest step so the rAF loop can re-schedule itself through the
  // ref rather than by a lexical self-reference (which the hooks lint rejects).
  const stepRef = useRef<(now: number) => void>(() => {})

  const drain = useCallback(() => {
    rafIdRef.current = null
    setIsRevealing(false)
    const onDrained = onDrainRef.current
    if (onDrained) {
      onDrainRef.current = null
      onDrained()
    }
  }, [])

  const step = useCallback(
    (now: number) => {
      const target = targetRef.current
      const remaining = target.length - displayedLenRef.current
      if (remaining <= 0) {
        drain()
        return
      }
      const dt = now - lastTickRef.current
      lastTickRef.current = now
      // Never slower than the floor; faster the further the reveal is behind.
      const charsPerSec = Math.max(minCharsPerSec, remaining * backlogGain)
      const advance = Math.min(remaining, Math.max(1, Math.round((charsPerSec * dt) / 1000)))
      let nextLen = displayedLenRef.current + advance
      // Don't stop between a surrogate pair (emoji etc.) — include the trailing
      // code unit so we never render half a code point for a frame.
      const lastUnit = target.charCodeAt(nextLen - 1)
      if (lastUnit >= 0xd800 && lastUnit <= 0xdbff && nextLen < target.length) {
        nextLen += 1
      }
      displayedLenRef.current = nextLen
      setText(target.slice(0, nextLen))
      rafIdRef.current = requestAnimationFrame(stepRef.current)
    },
    [minCharsPerSec, backlogGain, drain]
  )

  useEffect(() => {
    stepRef.current = step
  }, [step])

  const ensureRunning = useCallback(() => {
    if (rafIdRef.current !== null) return
    lastTickRef.current = performance.now()
    setIsRevealing(true)
    rafIdRef.current = requestAnimationFrame(stepRef.current)
  }, [])

  const reset = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = null
    }
    targetRef.current = ''
    displayedLenRef.current = 0
    onDrainRef.current = null
    setIsRevealing(false)
    setText('')
  }, [])

  const push = useCallback(
    (chunk: string) => {
      if (!chunk) return
      targetRef.current += chunk
      if (!enabled) {
        // Instant mode: reveal everything immediately, no animation.
        displayedLenRef.current = targetRef.current.length
        setText(targetRef.current)
        return
      }
      ensureRunning()
    },
    [enabled, ensureRunning]
  )

  const finish = useCallback(
    (onDrained?: () => void) => {
      const done = () => {
        onDrained?.()
        reset()
      }
      if (displayedLenRef.current >= targetRef.current.length) {
        done()
        return
      }
      onDrainRef.current = done
      ensureRunning()
    },
    [ensureRunning, reset]
  )

  // Stop the loop on unmount so we never write into an unmounted component.
  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current)
    }
  }, [])

  return { text, isRevealing, push, reset, finish }
}
